/**
 * Admin Push Notifications Page
 * Send and manage push notifications to users
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Bell,
  Send,
  Users,
  Truck,
  Shield,
  History,
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';

const AdminPushNotifications = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [subscriptionCounts, setSubscriptionCounts] = useState({
    total: 0,
    admins: 0,
    staff: 0,
    drivers: 0,
    others: 0
  });
  const [history, setHistory] = useState([]);
  
  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState('all');

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [countsRes, historyRes] = await Promise.all([
        api.get('/push/subscriptions/count'),
        api.get('/push/history')
      ]);
      setSubscriptionCounts(countsRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }

    setSending(true);
    try {
      const response = await api.post('/push/send', {
        title: title.trim(),
        body: body.trim(),
        target
      });
      
      toast.success(`Notification sent to ${response.data.sent} devices`);
      
      // Clear form and refresh data
      setTitle('');
      setBody('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const targetOptions = [
    { id: 'all', label: 'All Users', icon: Users, count: subscriptionCounts.total },
    { id: 'admins', label: 'Admins & CEOs', icon: Shield, count: subscriptionCounts.admins },
    { id: 'staff', label: 'Staff', icon: Users, count: subscriptionCounts.staff },
    { id: 'drivers', label: 'Drivers', icon: Truck, count: subscriptionCounts.drivers },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="push-notifications-page">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
                <Bell className="h-6 w-6 text-amber-400" />
                Push Notifications
              </h1>
              <p className="text-slate-400 text-sm">
                Send notifications to users' browsers
              </p>
            </div>
          </div>
          <Button
            onClick={fetchData}
            variant="ghost"
            className="text-slate-400 hover:text-white"
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        {/* Subscription Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {targetOptions.map(opt => {
            const Icon = opt.icon;
            return (
              <div 
                key={opt.id}
                className="bg-slate-900/50 border border-slate-800 rounded-lg p-4"
              >
                <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                  <Icon className="h-4 w-4" />
                  {opt.label}
                </div>
                <p className="text-2xl font-bold text-white">{opt.count}</p>
                <p className="text-slate-500 text-xs">subscribed</p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Send Notification Form */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Send className="h-5 w-5 text-amber-400" />
              Send Notification
            </h2>

            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Title *</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Notification title"
                  className="bg-slate-800 border-slate-700 text-white"
                  maxLength={100}
                  data-testid="notification-title"
                />
              </div>

              <div>
                <Label className="text-slate-300">Message *</Label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Notification message..."
                  className="w-full bg-slate-800 border border-slate-700 rounded-md p-3 text-white placeholder:text-slate-500 min-h-[100px] resize-none"
                  maxLength={500}
                  data-testid="notification-body"
                />
                <p className="text-slate-500 text-xs mt-1">{body.length}/500 characters</p>
              </div>

              <div>
                <Label className="text-slate-300">Target Audience</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {targetOptions.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setTarget(opt.id)}
                        className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                          target === opt.id
                            ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                            : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}
                        data-testid={`target-${opt.id}`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="text-sm">{opt.label}</span>
                        <span className="text-xs opacity-60">({opt.count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Button
                onClick={handleSend}
                disabled={sending || !title.trim() || !body.trim()}
                className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400"
                data-testid="send-notification-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Notification
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Notification History */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <History className="h-5 w-5 text-amber-400" />
              Recent Notifications
            </h2>

            {history.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No notifications sent yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {history.map((log, index) => (
                  <div
                    key={log.id || index}
                    className="bg-slate-800/50 rounded-lg p-3 border border-slate-700"
                    data-testid={`history-item-${index}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-white truncate">{log.title}</p>
                        <p className="text-slate-400 text-sm line-clamp-2">{log.body}</p>
                      </div>
                      <div className="flex items-center gap-1 text-xs shrink-0">
                        {log.sent_count > 0 ? (
                          <span className="text-teal-400 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            {log.sent_count}
                          </span>
                        ) : null}
                        {log.failed_count > 0 ? (
                          <span className="text-red-400 flex items-center gap-1">
                            <XCircle className="h-3 w-3" />
                            {log.failed_count}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                      <span className="capitalize">{log.target}</span>
                      <span>{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-8 bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <h3 className="text-blue-400 font-medium mb-2">How Push Notifications Work</h3>
          <ul className="text-slate-400 text-sm space-y-1">
            <li>• Users must enable notifications in their browser when prompted</li>
            <li>• Notifications work on Chrome, Firefox, and Edge (Safari has limited support)</li>
            <li>• Users will receive notifications even when the app is closed</li>
            <li>• Clicking a notification opens the app</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default AdminPushNotifications;
